import { useAccount, useMsal } from "@azure/msal-react";
import { loginRequest } from "../auth/authConfig";
import { useState, useEffect } from "react";

interface TokenState {
    token: string | null;
    isLoading: boolean;
    isAuthenticated: boolean;
    error: string | null;
}

const useToken = (): [() => Promise<string>, TokenState] => {
    const { instance, accounts, inProgress } = useMsal();
    const account = useAccount(accounts[0]);
    const [tokenState, setTokenState] = useState<TokenState>({
        token: null,
        isLoading: true,
        isAuthenticated: false,
        error: null
    });

    useEffect(() => {
        if (inProgress === "none" && accounts.length > 0) {
            setTokenState(prev => ({
                ...prev,
                isLoading: false,
                isAuthenticated: true
            }));
        } else if (inProgress === "none" && accounts.length === 0) {
            setTokenState(prev => ({
                ...prev,
                isLoading: false,
                isAuthenticated: false
            }));
        }
    }, [inProgress, accounts]);

    const getToken = async (): Promise<string> => {
        if (!account) {
            throw new Error('No authenticated account found');
        }

        try {
            const tokenResponse = await instance.acquireTokenSilent({
                ...loginRequest,
                account,
            });
            
            setTokenState(prev => ({
                ...prev,
                token: tokenResponse.accessToken,
                error: null
            }));
            
            return tokenResponse.accessToken;
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Token acquisition failed';
            setTokenState(prev => ({
                ...prev,
                token: null,
                error: errorMessage
            }));
            throw error;
        }
    };

    return [getToken, tokenState];
};

export default useToken;